import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Edit, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface Campaign {
  id: string;
  campaign_id: string;
  campaign_name: string;
  media_owner_name: string;
  start_date: string;
  end_date: string;
  total_budget: number;
  actual_reach: number;
  actual_engagement: number;
  actual_spent: number;
  performance_updated_at: string | null;
  status: string;
}

interface PerformanceListProps {
  campaigns: Campaign[];
  loading: boolean;
  onUpdate: (campaignId: string, reach: number, engagement: number, spent: number) => Promise<void>;
}

export const PerformanceList = ({ campaigns, loading, onUpdate }: PerformanceListProps) => {
  const { language } = useLanguage();
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [reach, setReach] = useState("");
  const [engagement, setEngagement] = useState("");
  const [spent, setSpent] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleEdit = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setReach(campaign.actual_reach?.toString() || "0");
    setEngagement(campaign.actual_engagement?.toString() || "0");
    setSpent(campaign.actual_spent?.toString() || "0");
    setIsOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedCampaign) return;

    setIsUpdating(true);
    try {
      await onUpdate(
        selectedCampaign.id,
        Number(reach),
        Number(engagement),
        Number(spent)
      );
      setIsOpen(false);
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {language === 'ar' ? 'لا توجد حملات' : 'No campaigns found'}
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border mt-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{language === 'ar' ? 'اسم الحملة' : 'Campaign Name'}</TableHead>
              <TableHead>{language === 'ar' ? 'الحالة' : 'Status'}</TableHead>
              <TableHead className="text-right">{language === 'ar' ? 'الوصول' : 'Reach'}</TableHead>
              <TableHead className="text-right">{language === 'ar' ? 'التفاعل' : 'Engagement'}</TableHead>
              <TableHead className="text-right">{language === 'ar' ? 'المصروف' : 'Spent'}</TableHead>
              <TableHead>{language === 'ar' ? 'آخر تحديث' : 'Last Updated'}</TableHead>
              <TableHead className="text-right">{language === 'ar' ? 'إجراءات' : 'Actions'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {campaigns.map((campaign) => (
              <TableRow key={campaign.id}>
                <TableCell className="font-medium">{campaign.campaign_name || 'Unnamed'}</TableCell>
                <TableCell>
                  <Badge variant={campaign.status === 'approved' ? 'default' : 'secondary'}>
                    {campaign.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {campaign.actual_reach?.toLocaleString() || '0'}
                </TableCell>
                <TableCell className="text-right">
                  {campaign.actual_engagement || '0'}%
                </TableCell>
                <TableCell className="text-right">
                  {campaign.actual_spent?.toLocaleString() || '0'} SAR
                </TableCell>
                <TableCell>
                  {campaign.performance_updated_at
                    ? new Date(campaign.performance_updated_at).toLocaleDateString()
                    : language === 'ar' ? 'لم يتم التحديث' : 'Not updated'}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(campaign)}
                    className="gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    {language === 'ar' ? 'تحرير' : 'Edit'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {language === 'ar' ? 'تحديث بيانات الأداء' : 'Update Performance Data'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الحملة' : 'Campaign'}</Label>
              <p className="text-sm font-medium">{selectedCampaign?.campaign_name}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reach">{language === 'ar' ? 'الوصول' : 'Reach'}</Label>
              <Input
                id="reach"
                type="number"
                value={reach}
                onChange={(e) => setReach(e.target.value)}
                placeholder="0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="engagement">{language === 'ar' ? 'معدل التفاعل (%)' : 'Engagement Rate (%)'}</Label>
              <Input
                id="engagement"
                type="number"
                step="0.1"
                value={engagement}
                onChange={(e) => setEngagement(e.target.value)}
                placeholder="0.0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="spent">{language === 'ar' ? 'المبلغ المصروف (ريال)' : 'Amount Spent (SAR)'}</Label>
              <Input
                id="spent"
                type="number"
                value={spent}
                onChange={(e) => setSpent(e.target.value)}
                placeholder="0"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={isUpdating}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button onClick={handleUpdate} disabled={isUpdating}>
                {isUpdating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {language === 'ar' ? 'حفظ' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
